import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BisZumEnde, SignatureField } from '@symphenia/ui-components'
import type { SiteDocPublicConfirm } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { alsDataUrl, publicConfirmDecline, publicConfirmFotoUrl, publicConfirmSign, publicConfirmView } from '../api/site-docs.api'
import { KanonAnsicht } from '../components/KanonAnsicht'
import '../arten'

const feld = 'h-11 w-full rounded-md border border-input bg-background px-3 text-base'

/**
 * AP-46 22.1 Nr. 3–5 — **Bestätigung per Link** im Browser des Kunden, ohne Anmeldung: der eingefrorene Inhalt
 * (gezeichnet aus dem kanonischen Stand wie die Vorzeige), die Fotos und der Bestätigungstext; „Bestätigen" erst
 * nach dem Lesen bis zum Ende, mit Name, Funktion und Unterschrift — oder ablehnen mit Grund.
 */
export default function BestaetigenPage() {
  const { t } = useTranslation()
  const { token = '' } = useParams<{ token: string }>()
  const [stand, setStand] = useState<SiteDocPublicConfirm | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gelesen, setGelesen] = useState(false)
  const [name, setName] = useState('')
  const [funktion, setFunktion] = useState('')
  const [bild, setBild] = useState<Blob | null>(null)
  const [ablehnen, setAblehnen] = useState(false)
  const [grund, setGrund] = useState('')
  const [arbeitet, setArbeitet] = useState(false)

  useEffect(() => {
    publicConfirmView(token)
      .then((s) => {
        setStand(s)
        if (s.name) setName(s.name)
      })
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [token, t])

  const tue = async (f: () => Promise<SiteDocPublicConfirm>) => {
    setArbeitet(true)
    setFehler(null)
    try {
      setStand(await f())
    } catch (e) {
      setFehler(fehlertext(e, t))
    } finally {
      setArbeitet(false)
    }
  }

  const kopf = stand?.tenantName ? <p className="text-sm font-medium text-muted-foreground">{stand.tenantName}</p> : null
  const was = stand?.kind && stand.number ? `${t(`siteDocs.kinds.${stand.kind}`)} ${stand.number}` : ''

  return (
    <div className="min-h-screen bg-background px-4 py-6 text-foreground">
      <main className="mx-auto max-w-3xl space-y-4">
        {kopf}
        <h1 className="text-2xl font-bold">{t('siteDocs.public.confirmTitle')}</h1>
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}
        {!stand && !fehler && <p className="text-muted-foreground">{t('common.loading')}</p>}
        {stand?.state === 'UNKNOWN' && <p>{t('siteDocs.public.unknown')}</p>}
        {stand?.state === 'EXPIRED' && <p>{t('siteDocs.public.expired', { betrieb: stand.tenantName ?? '' })}</p>}
        {stand?.state === 'ANSWERED' && <p className="text-lg">{t(stand.answer === 'DECLINED' ? 'siteDocs.public.declinedDone' : 'siteDocs.public.confirmedDone', { was })}</p>}
        {stand?.state === 'OPEN' && stand.objekt && (
          <>
            <p className="text-sm text-muted-foreground">{t('siteDocs.public.intro', { was, bis: stand.expiresAt ? stand.expiresAt.slice(0, 10).split('-').reverse().join('.') : '' })}</p>
            <BisZumEnde onGelesen={setGelesen} hinweis={t('siteDocs.release.readToEnd')} className="max-h-[55vh] rounded-md border p-3">
              <KanonAnsicht objekt={stand.objekt} />
              {stand.photos.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {stand.photos.map((f) => (
                    <figure key={f.id} className="space-y-1">
                      <img src={publicConfirmFotoUrl(token, f.id)} alt={f.caption ?? ''} className="w-full rounded-md border object-cover" loading="lazy" />
                      {f.caption && <figcaption className="text-xs text-muted-foreground">{f.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              )}
            </BisZumEnde>
            {ablehnen ? (
              <section className="space-y-2">
                <textarea className="min-h-24 w-full rounded-md border border-input bg-background p-3 text-base" placeholder={t('siteDocs.release.reason')} value={grund} onChange={(e) => setGrund(e.target.value)} />
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setAblehnen(false)} className="h-11 rounded-md border px-4 text-sm">
                    {t('common.cancel')}
                  </button>
                  <button type="button" disabled={grund.trim().length < 3 || arbeitet} onClick={() => void tue(() => publicConfirmDecline(token, { reason: grund.trim(), name: name.trim() || null }))} className="h-11 rounded-md bg-red-600 px-4 text-sm font-medium text-white disabled:opacity-50">
                    {t('siteDocs.public.sendDecline')}
                  </button>
                </div>
              </section>
            ) : (
              <section className="space-y-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <input className={feld} placeholder={t('siteDocs.release.name')} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                  <input className={feld} placeholder={t('siteDocs.release.function')} value={funktion} onChange={(e) => setFunktion(e.target.value)} />
                </div>
                <SignatureField onChange={setBild} label={t('siteDocs.release.signature')} loeschenLabel={t('siteDocs.release.clear')} zuKurzLabel={t('siteDocs.release.tooShort')} />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button type="button" onClick={() => setAblehnen(true)} className="h-11 rounded-md border px-4 text-sm text-red-700">
                    {t('siteDocs.public.decline')}
                  </button>
                  <div className="flex items-center gap-2">
                    {!gelesen && <span className="text-xs text-muted-foreground">{t('siteDocs.release.readFirst')}</span>}
                    <button
                      type="button"
                      disabled={!gelesen || !name.trim() || !bild || arbeitet}
                      onClick={() => void tue(async () => publicConfirmSign(token, { name: name.trim(), function: funktion.trim() || null, image: await alsDataUrl(bild!) }))}
                      className="h-11 rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    >
                      {t('siteDocs.release.confirm')}
                    </button>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  )
}
